import { useEffect, useRef, useState } from "react";
import { exportTable } from "../api";
import { useStore } from "../store";

type Format = "xlsx" | "csv";

/**
 * Per-table export, the way Power BI does it: a control on the visual itself
 * rather than one download for the whole page.
 *
 * The export does NOT reuse the rows on screen. It re-runs the widget's SQL
 * through the export endpoint, which the server gates at its export row cap
 * instead of the screen cap -- so a table showing the first 100,000 of 823,286
 * rows still exports all of them. Exporting what happens to be rendered would
 * quietly hand someone a truncated financial extract.
 */
export function ExportMenu({ sql, title }: { sql: string; title: string }) {
  const connId = useStore((s) => s.activeConnectionId);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Format | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  async function run(format: Format) {
    if (!connId || busy) return;
    setBusy(format);
    setError(null);
    try {
      const saved = await exportTable(connId, sql, format, title);
      // Only close on a real download. A cancelled save leaves the menu open,
      // so the click does not read as "nothing happened" — the previous
      // behaviour closed either way and looked identical to a silent failure.
      if (saved) setOpen(false);
    } catch (e) {
      // Keep the menu open so the message has somewhere to live.
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="export-menu" ref={wrapRef}>
      <button
        className="widget-action"
        title="Export this table"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        disabled={!connId}
      >
        &#x2b73;
      </button>
      {open && (
        <div className="export-pop" role="menu">
          <button role="menuitem" disabled={busy !== null} onClick={() => run("xlsx")}>
            {busy === "xlsx" ? "Exporting..." : "Excel (.xlsx)"}
          </button>
          <button role="menuitem" disabled={busy !== null} onClick={() => run("csv")}>
            {busy === "csv" ? "Exporting..." : "CSV (.csv)"}
          </button>
          <div className="export-note">Exports every row, not just those shown</div>
          {error && <div className="export-err">{error}</div>}
        </div>
      )}
    </div>
  );
}
